
import React, { useState } from "react";
import Query from './Query';
import {
  Box, Typography, Container, Button, Modal, Grid, Paper,
  Card, CardContent, Avatar, List, ListItem, ListItemIcon,
  ListItemText, Divider, Chip, ThemeProvider, createTheme
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import TimelineIcon from "@mui/icons-material/Timeline";
import CodeIcon from "@mui/icons-material/Code";

// Import banners
import javaBanner from "../img/aaaservices/javanew.jpg";
import pythonBanner from "../img/aaaservices/pythonnew.jpg";
import webBanner from "./international.jpg";
import databaseBanner from "../img/aaaservices/databsedevlp.jpg";
import aiBanner from "./international.jpg";
import dataBanner from "./international.jpg";
import any from "./international.jpg";
import cloud from "./international.jpg";


// Create a professional theme with blue background
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      dark: '#0d47a1',
      light: '#42a5f5',
    },
    background: {
      default: 'blue',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a2c3e',
      secondary: '#546e7a',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.85rem',
        },
      },
    },
  },
});

// Internship Data
const internshipData = {
  java: {
    id: "java",
    title: "Java Developer",
    subtitle: "Build scalable backend services for AI-powered educational platform",
    banner: javaBanner,
    about: `Build scalable backend services and contribute to our AI-powered educational platform using Java and Spring Boot. 
    You'll work on high-performance microservices that power our learning management system, handling thousands of concurrent users.`,
    responsibilities: [
      "Develop RESTful APIs and microservices using Spring Boot",
      "Ensure APIs are optimized for high performance and scalability",
      "Integrate APIs with AI-powered features and machine learning models",
      "Collaborate with front-end developers to ensure seamless user experience",
      "Participate in code reviews and contribute to technical documentation",
      "Debug and fix issues in existing applications",
      "Learn and implement best practices for Java development"
    ],
    benefits: [
      { icon: <TimelineIcon />, title: "Flexible Work Hours", description: "Work when you're most productive" },
      { icon: <WorkIcon />, title: "Competitive Stipend", description: "Rewarding compensation package" },
      { icon: <SchoolIcon />, title: "Certificate of Completion", description: "Industry-recognized certification" },
      { icon: <CodeIcon />, title: "Opportunity for PPO", description: "Path to full-time employment" }
    ],
    testimonials: [
      {
        name: "Shubham Kumar",
        role: "Former Java intern",
        content: "My internship at Paperhai was transformative. The mentorship and hands-on experience helped me grow tremendously as a developer.",
        avatar: "SK"
      },
      {
        name: "Vikash Kumar",
        role: "Software Engineer",
        content: "Started as an intern and now I'm a full-time engineer. Paperhai truly invests in their interns' growth and future.",
        avatar: "VK"
      }
    ],
    requirements: [
      "Strong knowledge of Core Java and OOP concepts",
      "Experience with Spring Boot and Spring Framework",
      "Understanding of RESTful APIs and microservices architecture",
      "Knowledge of databases (SQL/NoSQL)",
      "Familiarity with version control systems (Git)",
      "Good problem-solving and analytical skills",
      "Excellent communication and teamwork abilities"
    ],
    techStack: ["Java", "Spring Boot", "Microservices", "REST APIs", "MySQL", "MongoDB", "Docker", "Git"]
  },
  python: {
    id: "python",
    title: "Python Developer",
    subtitle: "Build scalable data pipelines and backend services using Python",
    banner: pythonBanner,
    about: "Work on backend systems, data pipelines, and automation using Python and related frameworks. You'll build robust applications that process large volumes of educational data.",
    responsibilities: [
      "Develop backend services using Python and Django/FastAPI",
      "Build data processing pipelines for analytics",
      "Create automation scripts for various business processes",
      "Integrate with machine learning models for content recommendation",
      "Optimize application performance and response times",
      "Write clean, maintainable, and well-documented code",
      "Collaborate with cross-functional teams"
    ],
    benefits: [
      { icon: <TimelineIcon />, title: "Flexible Work Hours", description: "Work when you're most productive" },
      { icon: <WorkIcon />, title: "Competitive Stipend", description: "Rewarding compensation package" },
      { icon: <SchoolIcon />, title: "Certificate of Completion", description: "Industry-recognized certification" },
      { icon: <CodeIcon />, title: "Opportunity for PPO", description: "Path to full-time employment" }
    ],
    testimonials: [
      {
        name: "Shubham Kumar",
        role: "Former Java intern",
        content: "My internship at Paperhai was transformative. The mentorship and hands-on experience helped me grow tremendously as a developer.",
        avatar: "SK"
      },
      {
        name: "Vikash Kumar",
        role: "Software Engineer",
        content: "Started as an intern and now I'm a full-time engineer. Paperhai truly invests in their interns' growth and future.",
        avatar: "VK"
      }
    ],
    requirements: [
      "Proficiency in Python programming",
      "Experience with Django or FastAPI frameworks",
      "Understanding of RESTful APIs",
      "Knowledge of databases and ORM",
      "Familiarity with data processing libraries (Pandas, NumPy)",
      "Experience with version control (Git)",
      "Strong analytical and problem-solving skills"
    ],
    techStack: ["Python", "Django", "FastAPI", "PostgreSQL", "Pandas", "Docker", "Redis", "Git"]
  },
  web: {
    id: "web",
    title: "Web Development",
    subtitle: "Create responsive and interactive web applications",
    banner: webBanner,
    about: "Develop responsive web applications using modern JavaScript frameworks. You'll work on the frontend of our educational platform, creating intuitive user interfaces.",
    responsibilities: [
      "Build responsive user interfaces with React.js",
      "Implement state management using Redux or Context API",
      "Create reusable components and libraries",
      "Optimize applications for maximum performance",
      "Collaborate with backend developers for API integration",
      "Ensure cross-browser compatibility",
      "Write clean and maintainable code"
    ],
    benefits: [
      { icon: <TimelineIcon />, title: "Flexible Work Hours", description: "Work when you're most productive" },
      { icon: <WorkIcon />, title: "Competitive Stipend", description: "Rewarding compensation package" },
      { icon: <SchoolIcon />, title: "Certificate of Completion", description: "Industry-recognized certification" },
      { icon: <CodeIcon />, title: "Opportunity for PPO", description: "Path to full-time employment" }
    ],
    testimonials: [
      {
        name: "Shubham Kumar",
        role: "Former Java intern",
        content: "My internship at Paperhai was transformative. The mentorship and hands-on experience helped me grow tremendously as a developer.",
        avatar: "SK"
      },
      {
        name: "Vikash Kumar",
        role: "Software Engineer",
        content: "Started as an intern and now I'm a full-time engineer. Paperhai truly invests in their interns' growth and future.",
        avatar: "VK"
      }
    ],
    requirements: [
      "Strong knowledge of HTML5, CSS3, and JavaScript",
      "Experience with React.js and its ecosystem",
      "Understanding of responsive design principles",
      "Familiarity with RESTful APIs",
      "Knowledge of version control (Git)",
      "Experience with modern build tools",
      "Good eye for UI/UX design"
    ],
    techStack: ["React", "JavaScript", "TypeScript", "Redux", "HTML5", "CSS3", "Material-UI", "Webpack"]
  },
  database: {
    id: "database",
    title: "Database Developer",
    subtitle: "Design and optimize database systems for analytics",
    banner: databaseBanner,
    about: "Design and optimize database systems for analytics. You'll work with both SQL and NoSQL databases to store and retrieve educational content efficiently.",
    responsibilities: [
      "Design database schemas for various applications",
      "Write complex SQL queries and optimize them",
      "Implement database security and backup strategies",
      "Work with both SQL and NoSQL databases",
      "Monitor database performance and tune as needed",
      "Create database documentation",
      "Collaborate with development team for data requirements"
    ],
    benefits: [
      { icon: <TimelineIcon />, title: "Flexible Work Hours", description: "Work when you're most productive" },
      { icon: <WorkIcon />, title: "Competitive Stipend", description: "Rewarding compensation package" },
      { icon: <SchoolIcon />, title: "Certificate of Completion", description: "Industry-recognized certification" },
      { icon: <CodeIcon />, title: "Opportunity for PPO", description: "Path to full-time employment" }
    ],
    testimonials: [
      {
        name: "Shubham Kumar",
        role: "Former Java intern",
        content: "My internship at Paperhai was transformative. The mentorship and hands-on experience helped me grow tremendously as a developer.",
        avatar: "SK"
      },
      {
        name: "Vikash Kumar",
        role: "Software Engineer",
        content: "Started as an intern and now I'm a full-time engineer. Paperhai truly invests in their interns' growth and future.",
        avatar: "VK"
      }
    ],
    requirements: [
      "Strong knowledge of SQL and database design",
      "Experience with MySQL, PostgreSQL, or MongoDB",
      "Understanding of data modeling principles",
      "Knowledge of indexing and query optimization",
      "Familiarity with backup and recovery procedures",
      "Experience with data migration tools",
      "Attention to detail and analytical mindset"
    ],
    techStack: ["MySQL", "PostgreSQL", "MongoDB", "Redis", "SQL", "Database Design", "Data Modeling", "ETL"]
  },
  ai: {
    id: "ai",
    title: "AI/ML Developer",
    subtitle: "Develop machine learning algorithms for educational platform",
    banner: aiBanner,
    about: "Develop machine learning algorithms and recommendation systems that personalize the learning experience for thousands of students.",
    responsibilities: [
      "Develop machine learning models for content recommendation",
      "Implement NLP algorithms for text analysis",
      "Process and clean large datasets for training",
      "Evaluate model performance and fine-tune parameters",
      "Deploy ML models to production",
      "Stay updated with latest ML research",
      "Collaborate with backend team for model integration"
    ],
    benefits: [
      { icon: <TimelineIcon />, title: "Flexible Work Hours", description: "Work when you're most productive" },
      { icon: <WorkIcon />, title: "Competitive Stipend", description: "Rewarding compensation package" },
      { icon: <SchoolIcon />, title: "Certificate of Completion", description: "Industry-recognized certification" },
      { icon: <CodeIcon />, title: "Opportunity for PPO", description: "Path to full-time employment" }
    ],
    testimonials: [
      {
        name: "Shubham Kumar",
        role: "Former Java intern",
        content: "My internship at Paperhai was transformative. The mentorship and hands-on experience helped me grow tremendously as a developer.",
        avatar: "SK"
      },
      {
        name: "Vikash Kumar",
        role: "Software Engineer",
        content: "Started as an intern and now I'm a full-time engineer. Paperhai truly invests in their interns' growth and future.",
        avatar: "VK"
      }
    ],
    requirements: [
      "Strong understanding of machine learning concepts",
      "Experience with Python and ML libraries (scikit-learn, TensorFlow, PyTorch)",
      "Knowledge of NLP techniques",
      "Understanding of statistics and mathematics",
      "Experience with data preprocessing",
      "Familiarity with model deployment",
      "Research-oriented mindset"
    ],
    techStack: ["Python", "TensorFlow", "PyTorch", "scikit-learn", "NLP", "Pandas", "NumPy", "Jupyter"]
  },
  data: {
    id: "data",
    title: "Data Analytics",
    subtitle: "Analyze data and generate insights for business decisions",
    banner: any,
    about: "Analyze educational data and generate insights for business decisions. You'll work with large datasets to identify trends and improve learning outcomes.",
    responsibilities: [
      "Analyze user behavior and learning patterns",
      "Create dashboards and reports for stakeholders",
      "Perform statistical analysis on educational data",
      "Identify trends and insights from data",
      "Work with data visualization tools",
      "Clean and prepare data for analysis",
      "Present findings to the team"
    ],
    benefits: [
      { icon: <TimelineIcon />, title: "Flexible Work Hours", description: "Work when you're most productive" },
      { icon: <WorkIcon />, title: "Competitive Stipend", description: "Rewarding compensation package" },
      { icon: <SchoolIcon />, title: "Certificate of Completion", description: "Industry-recognized certification" },
      { icon: <CodeIcon />, title: "Opportunity for PPO", description: "Path to full-time employment" }
    ],
    testimonials: [
      {
        name: "Shubham Kumar",
        role: "Former Java intern",
        content: "My internship at Paperhai was transformative. The mentorship and hands-on experience helped me grow tremendously as a developer.",
        avatar: "SK"
      },
      {
        name: "Vikash Kumar",
        role: "Software Engineer",
        content: "Started as an intern and now I'm a full-time engineer. Paperhai truly invests in their interns' growth and future.",
        avatar: "VK"
      }
    ],
    requirements: [
      "Proficiency in SQL and data querying",
      "Experience with Python or R for data analysis",
      "Knowledge of statistical methods",
      "Familiarity with data visualization tools",
      "Understanding of business metrics",
      "Strong analytical and problem-solving skills",
      "Attention to detail"
    ],
    techStack: ["SQL", "Python", "Pandas", "Tableau", "Power BI", "Excel", "Statistics", "Data Visualization"]
  },
  dataScience: {
    id: "datascience",
    title: "Data Science",
    subtitle: "Work on data-driven solutions and machine learning models",
    banner: any,
    about: "Work with large datasets to build predictive models and extract meaningful insights. You'll use statistical techniques and machine learning to solve real-world problems in the education domain.",
    responsibilities: [
      "Analyze large datasets to discover trends and patterns",
      "Build machine learning models for prediction and classification",
      "Clean and preprocess raw data for analysis",
      "Work with data visualization tools to present insights",
      "Collaborate with engineering teams to deploy models",
      "Perform exploratory data analysis (EDA)",
      "Document experiments and results"
    ],
    benefits: [
      { icon: <TimelineIcon />, title: "Flexible Work Hours", description: "Work when you're most productive" },
      { icon: <WorkIcon />, title: "Competitive Stipend", description: "Rewarding compensation package" },
      { icon: <SchoolIcon />, title: "Certificate of Completion", description: "Industry-recognized certification" },
      { icon: <CodeIcon />, title: "Opportunity for PPO", description: "Path to full-time employment" }
    ],
    testimonials: [
      {
        name: "Aman Verma",
        role: "Data Science Intern",
        content: "The internship gave me real experience working with datasets and machine learning models.",
        avatar: "AV"
      },
      {
        name: "Neha Singh",
        role: "Data Analyst",
        content: "I learned practical data analysis and visualization skills that helped me land my first job.",
        avatar: "NS"
      }
    ],
    requirements: [
      "Strong knowledge of Python programming",
      "Understanding of statistics and probability",
      "Experience with Pandas, NumPy, and Matplotlib",
      "Basic knowledge of machine learning algorithms",
      "Experience with data visualization tools",
      "Familiarity with SQL",
      "Analytical and problem-solving skills"
    ],
    techStack: ["Python", "Pandas", "NumPy", "Scikit-learn", "Matplotlib", "SQL", "Jupyter", "Statistics"]
  },

  cloud: {
    id: "cloud",
    title: "Cloud Computing",
    subtitle: "Build and manage scalable cloud infrastructure",
    banner: cloud,
    about: "Work on modern cloud platforms to deploy and manage scalable applications. You'll gain hands-on experience with cloud infrastructure, containerization, and CI/CD pipelines.",
    responsibilities: [
      "Deploy applications on cloud platforms (AWS/Azure/GCP)",
      "Manage cloud infrastructure and resources",
      "Work with containerization tools like Docker",
      "Set up CI/CD pipelines for automated deployment",
      "Monitor application performance in the cloud",
      "Ensure security and scalability of cloud systems",
      "Collaborate with development teams"
    ],
    benefits: [
      { icon: <TimelineIcon />, title: "Flexible Work Hours", description: "Work when you're most productive" },
      { icon: <WorkIcon />, title: "Competitive Stipend", description: "Rewarding compensation package" },
      { icon: <SchoolIcon />, title: "Certificate of Completion", description: "Industry-recognized certification" },
      { icon: <CodeIcon />, title: "Opportunity for PPO", description: "Path to full-time employment" }
    ],
    testimonials: [
      {
        name: "Rahul Sharma",
        role: "Cloud Intern",
        content: "This internship helped me understand real-world cloud infrastructure and DevOps practices.",
        avatar: "RS"
      },
      {
        name: "Priya Mehta",
        role: "DevOps Engineer",
        content: "I started as an intern and gained hands-on experience with AWS and Docker.",
        avatar: "PM"
      }
    ],
    requirements: [
      "Basic knowledge of cloud platforms (AWS/Azure/GCP)",
      "Understanding of Linux systems",
      "Experience with Docker or containerization",
      "Basic knowledge of networking concepts",
      "Understanding of CI/CD pipelines",
      "Familiarity with Git",
      "Problem-solving mindset"
    ],
    techStack: ["AWS", "Azure", "GCP", "Docker", "Kubernetes", "Linux", "CI/CD", "Terraform"]
  }
};

const InternshipDetails = () => {
  const { id } = useParams();
  const internship = internshipData[id];
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  if (!internship) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 8 }}>
          <Container maxWidth="md" sx={{ textAlign: "center" }}>
            <Card sx={{ p: { xs: 4, md: 6 } }}>
              <Typography variant="h4" gutterBottom fontWeight={600}>
                Internship Not Found
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                The internship you're looking for doesn't exist or has been removed.
              </Typography>
              <Button
              onClick={() =>
                navigate("/", {
                  state: { scrollToInternships: true }
                })
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "rgba(255,255,255,0.9)",
                color: "#1976d2",
                mb: 3,
                "&:hover": { backgroundColor: "#fff" }
              }}
            >
              ← Back to Internships
            </Button>
            </Card>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        {/* Banner Section */}
        <Box
          sx={{
            width: "100%",
            height: { xs: "350px", sm: "400px", md: "500px" },
            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.4)), url(${internship.banner})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Container maxWidth="lg" sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Button
              onClick={() =>
                navigate("/", {
                  state: { scrollToInternships: true }
                })
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "rgba(255,255,255,0.9)",
                color: "#1976d2",
                mb: 3,
                "&:hover": { backgroundColor: "#fff" }
              }}
            >
              ← Back to Internships
            </Button>
            <Typography
              variant="h2"
              sx={{
                color: "#fff",
                fontWeight: 800,
                mb: 2,
                textShadow: "2px 2px 8px rgba(0,0,0,0.3)",
                fontSize: { xs: "2rem", sm: "3rem", md: "4rem" }
              }}
            >
              {internship.title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "#fff",
                maxWidth: "700px",
                mx: "auto",
                textShadow: "1px 1px 4px rgba(0,0,0,0.3)",
                opacity: 0.95,
                mb: 4,
                fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" }
              }}
            >
              {internship.subtitle}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleOpen}
              sx={{
                py: { xs: 1, md: 1.5 },
                px: { xs: 4, md: 6 },
                fontSize: { xs: "1rem", md: "1.2rem" },
                borderRadius: 3,
                boxShadow: 4,
                fontWeight: 600,
                "&:hover": { transform: "scale(1.02)", boxShadow: 6 }
              }}
            >
              Apply Now
            </Button>
          </Container>
        </Box>

        {/* Content Container - White Cards on Blue Background */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>

          {/* About Section Card */}
          <Card sx={{ p: { xs: 3, md: 4 }, mb: 4 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: "text.primary", fontSize: { xs: "1.75rem", md: "2rem" } }}>
              About This Role
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.7, color: "text.secondary" }}>
              {internship.about}
            </Typography>
          </Card>

          {/* Responsibilities Card */}
          <Card sx={{ p: { xs: 3, md: 4 }, mb: 4 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: "1.75rem", md: "2rem" } }}>
              Responsibilities
            </Typography>
            <List>
              {internship.responsibilities.map((item, index) => (
                <ListItem key={index} sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <CheckCircleIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ sx: { fontSize: { xs: "0.95rem", md: "1rem" }, color: "text.primary" } }}
                  />
                </ListItem>
              ))}
            </List>
          </Card>

          {/* Benefits Card */}
          <Card sx={{ p: { xs: 3, md: 4 }, mb: 4 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: "1.75rem", md: "2rem" } }}>
              Benefits
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {internship.benefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Avatar sx={{ bgcolor: "primary.light", width: 64, height: 64, mx: "auto", mb: 2 }}>
                      <Box sx={{ color: "#fff" }}>{benefit.icon}</Box>
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>

          {/* Requirements Card */}
          <Card sx={{ p: { xs: 3, md: 4 }, mb: 4 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: "1.75rem", md: "2rem" } }}>
              Requirements
            </Typography>
            <List>
              {internship.requirements.map((item, index) => (
                <ListItem key={index} sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <CheckCircleIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ sx: { fontSize: { xs: "0.95rem", md: "1rem" }, color: "text.primary" } }}
                  />
                </ListItem>
              ))}
            </List>
          </Card>

          {/* Tech Stack Card */}
          <Card sx={{ p: { xs: 3, md: 4 }, mb: 4 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: "1.75rem", md: "2rem" } }}>
              Required Skills
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
              {internship.techStack.map((tech, index) => (
                <Chip
                  key={index}
                  label={tech}
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontSize: { xs: "0.8rem", md: "0.9rem" },
                    py: 1.5,
                    px: 1,
                    borderRadius: 2
                  }}
                />
              ))}
            </Box>
          </Card>

          {/* Testimonials Card */}
          {/* <Card sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: "1.75rem", md: "2rem" } }}>
              What Interns Say
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {internship.testimonials.map((testimonial, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card variant="outlined" sx={{ p: 3, height: "100%", bgcolor: "#f8fafc" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body1" sx={{ fontStyle: "italic", color: "text.primary" }}>
                      "{testimonial.content}"
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Card> */}
        </Container>

        {/* Apply Modal */}
        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 400, md: 450 },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="h5" fontWeight={700} mb={2}>
              Apply for {internship.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              <strong>Email your Updated CV</strong> to <br />
              <a href="mailto:info.iiautomate@gmail.com" style={{ textDecoration: "none", color: theme.palette.primary.main }}>
                info.iiautomate@gmail.com
              </a>
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              component="a"
              href={`mailto:info.iiautomate@gmail.com?subject=Application%20for%20${encodeURIComponent(
                internship.title
              )}&body=Dear%20HR,%0D%0A%0D%0AI am interested in the ${encodeURIComponent(
                internship.title
              )} position. Please find my CV attached.%0D%0A%0D%0AThank you.`}
              sx={{ py: 1.5, fontWeight: 600 }}
            >
              Send Your CV
            </Button>
          </Box>
        </Modal>
      </Box>
      <Query />
    </ThemeProvider>
  );
};

export default InternshipDetails;




